import { supabase } from "@/lib/supabase";
import { captureClientEnvironment, formattedAppVersion } from "@/lib/clientEnvironment";

/**
 * Home-grown error tracking (backlog ADMIN-9) — not a third-party SDK,
 * matching the rest of this codebase's hand-rolled-fetch style. Captures
 * errors only from authenticated, signed-in users (a deliberate v1 scope
 * cut — pre-login/landing-page errors aren't covered here); a public
 * unauthenticated capture endpoint would need its own rate-limiting/abuse
 * work that doesn't fit a "basic observability" bar.
 */

/** Bounds how many reports one page load can generate — a render-loop or crash-loop shouldn't be able to hammer the network. */
const MAX_REPORTS_PER_LOAD = 10;
let reportCount = 0;

export interface ReportErrorInput {
  message: string;
  stack?: string | null;
  name?: string | null;
  /** First line of a React error boundary's componentStack, if this came from one. */
  component?: string | null;
}

/**
 * Inserts an error_events row (if signed in) and best-effort triggers the
 * alert email via notify-error. Never throws — a failure here must not
 * recurse into the very error-reporting path that's supposed to catch it.
 */
export async function reportError(input: ReportErrorInput): Promise<void> {
  try {
    if (reportCount >= MAX_REPORTS_PER_LOAD) return;
    reportCount += 1;

    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData.user) return; // signed out — out of scope for v1

    const env = captureClientEnvironment();
    const { data, error } = await supabase
      .from("error_events")
      .insert({
        source: "frontend",
        message: input.message.slice(0, 2000),
        name: input.name ?? null,
        stack: input.stack ? input.stack.slice(0, 8000) : null,
        route: window.location.pathname,
        component: input.component ?? null,
        user_agent: env.userAgent,
        app_version: formattedAppVersion(),
        user_id: userData.user.id,
        user_email: userData.user.email ?? "",
      })
      .select("id")
      .single();
    if (error || !data) {
      console.error("reportError: insert failed", error);
      return;
    }

    // Fire-and-forget — never awaited by the caller, and the .catch() is
    // required: an uncaught rejection here would itself become an
    // unhandledrejection that the global listener would try to report,
    // the one real recursion risk in this design.
    supabase.functions.invoke("notify-error", { body: { errorEventId: data.id } }).catch(console.error);
  } catch (e) {
    console.error("reportError failed (swallowed):", e);
  }
}
